import { Button, Card, Text } from '@/ui'
import { TypographyStyles, TextColors } from '@/styles'
import { useLaunchHaven } from '@/features/adviser-chat/hooks/useLaunchHaven'
import {
  HAVEN_ENTRY_POINT,
  HAVEN_LAUNCH_ACTION,
} from '@/features/adviser-chat/lib/types'
import { useAdviserAnalytics } from '@/features/adviser-chat/analytics/use-adviser-analytics'

/**
 * Copy branches on whether there is a number to talk about. "Ask Haven about my
 * estimate" promises the one thing a member without an estimate is missing, and
 * the members with no estimate are the ones this entry point helps most —
 * "why don't I have an estimate yet" was 4 of 12 tickets in the support review
 * behind CORE-7823.
 *
 * Placeholder wording pending design/content; the branching is the part that
 * matters here.
 */
const COPY = {
  withEstimate: {
    heading: 'Questions about this estimate?',
    body: 'Haven can walk through how this number was calculated, what changed since last quarter, and what to do if you can’t pay it in full.',
    cta: 'Ask Haven about my estimate',
  },
  withoutEstimate: {
    heading: 'Not sure why you don’t have an estimate?',
    body: 'Haven can explain what’s still outstanding, what it means for this quarter, and what your options are.',
    cta: 'Ask Haven why',
  },
} as const

export const AskHavenAboutEstimate = ({
  hasEstimate,
}: {
  hasEstimate: boolean
}) => {
  const launchHaven = useLaunchHaven()
  const { trackEntryPointClicked } = useAdviserAnalytics()

  const copy = hasEstimate ? COPY.withEstimate : COPY.withoutEstimate

  const onClick = () => {
    trackEntryPointClicked({
      launchContext: HAVEN_LAUNCH_ACTION.quarterlyEstimates,
      entryPoint: HAVEN_ENTRY_POINT.taxesQuarterly,
    })
    launchHaven({
      context: {
        type: 'action',
        action: HAVEN_LAUNCH_ACTION.quarterlyEstimates,
      },
      entryPoint: HAVEN_ENTRY_POINT.taxesQuarterly,
    })
  }

  return (
    <Card>
      <div className="flex flex-col gap-2">
        <Text variant={TypographyStyles.H3}>{copy.heading}</Text>
        <Text variant={TypographyStyles.BODY_MD} color={TextColors.MUTED}>
          {copy.body}
        </Text>
        <div>
          <Button onClick={onClick}>{copy.cta}</Button>
        </div>
      </div>
    </Card>
  )
}
